import { useCallback, useContext, useEffect, useRef, useState, type RefCallback } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { useIsConversationCancelled } from '@atlassian/conversation-assistant-store/controllers/conversation-cancellation';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useSubscriptionInterop } from '@confluence/apollo-relay-interop/entry-points/useSubscriptionInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	useOptimisticCommentAgentSession,
	getOptimisticCommentAgentIdentity,
	type CommentAgentIdentity,
} from './optimisticCommentAgentSession.ts';
import { useCommentAgentSessionPolling } from './useCommentAgentSessionPolling.ts';
import { AgentSessionPollingContext } from './AgentSessionPollingProvider.tsx';
import { SharedCommentAgentSessionsContext } from './sharedCommentAgentSessions.ts';
import {
	fetchCommentAgentSessionState,
	type pollCommentAgentSessions,
} from './commentAgentSessionPolling.ts';
import { useSubmittedCommentAgentAction } from './submittedCommentAgentAction.ts';
import { AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT } from './agentMentionConstants.ts';
import type { useCommentAgentSessionStatusSubscription as CommentAgentSessionStatusSubscriptionType } from './__generated__/useCommentAgentSessionStatusSubscription.graphql.ts';

/**
 * Live status subscription for a single comment's agent conversation.
 *
 * This is an *observe-only* channel: it streams session-status updates for the given
 * conversation without competing with the conversation's own message stream (owned by the
 * open CwR sidebar right after a mention) — which is what made a direct POST to the chat
 * `message/stream/sse` endpoint unreliable for freshly-mentioned agents.
 *
 * The union member's `__typename` drives our ConversationState:
 *   - ConvoAiTraceMessage                                  → WORKING
 *   - ConvoAiConversationMessage (state COMPLETED)         → hidden
 *   - ConvoAiConversationMessage (state CANCELLED)         → hidden (experiment on)
 *   - ConvoAiConversationMessage (state FAILED)            → FAILED
 *   - ConvoAiConversationMessage (ASSISTANT, has actions)  → INPUT_REQUIRED
 *   - ConvoAiConversationMessage (ASSISTANT, no actions)   → ARTEFACT_READY
 *   - ConvoAiErrorMessage                                  → FAILED
 *   - ConvoAiEmptyConversation / other                     → ignored
 *
 * Modelled on jira-ai/agent-stack-unconnected's AgentSessionStatusSubscription. Defined inline
 * (not a separate module) per graphql-relay-compat/no-import-relay-graphql-operations.
 */
const CommentAgentSessionStatusSubscription = graphql`
	subscription useCommentAgentSessionStatusSubscription($cloudId: ID!, $conversationId: ID!) {
		convoai_onAgentSessionUpdate(cloudId: $cloudId, conversationId: $conversationId)
			@optIn(to: "ConvoAiAgentSessionUpdate") {
			__typename
			... on ConvoAiTraceMessage {
				messageTemplate
			}
			... on ConvoAiConversationMessage {
				role
				agentSessionState
				actions {
					key
				}
			}
			... on ConvoAiErrorMessage {
				errorMessageTemplate: messageTemplate
				statusCode
			}
		}
	}
`;

/**
 * The four conversation states surfaced for a comment's agent session.
 *
 * Modelled after the A2A task state spec:
 * - WORKING        — agent is actively processing
 * - ARTEFACT_READY — agent finished cleanly; no user action required
 * - INPUT_REQUIRED — user must resolve pending actions or a policy block
 * - FAILED         — the agent reported an error
 *
 * See: https://hello.atlassian.net/wiki/spaces/AIC1/pages/7123446755
 */
export type ConversationState = 'WORKING' | 'ARTEFACT_READY' | 'INPUT_REQUIRED' | 'FAILED';

export type CommentAgentSession = {
	/** Attach to the rendered status row; off-screen sessions do not poll. */
	sessionStatusRef?: RefCallback<HTMLDivElement>;
	/**
	 * The derived conversation state, or null when there is no conversation to track yet
	 * (the BE association is not available).
	 */
	state: ConversationState | 'THINKING' | null;
	/** Mention identity before CAP creates the session association. */
	agentIdentity?: CommentAgentIdentity;
	/** The conversationId this session is tracking. */
	conversationId: string | undefined;
};

// Snapshot states for which the agent's turn has genuinely ended, so there is nothing left to
// stream — we surface the snapshot directly and skip opening a live subscription (a perf win on
// page load, where most sessions are already settled).
//
// INPUT_REQUIRED is deliberately excluded: it is NOT terminal — it means the agent is paused
// waiting for the user. When the user replies (via the Rovo sidebar) the conversation resumes
// with fresh WORKING → ARTEFACT_READY messages, so the subscription must stay open to receive
// them. Treating it as terminal would freeze the badge on "Waiting for your input" for the
// rest of the session.
const TERMINAL_STATES = new Set<ConversationState>(['ARTEFACT_READY', 'FAILED']);

const COMPLETION_GRACE_MS = 500;

export type StreamState = ConversationState | 'COMPLETED' | 'CANCELLED';

type SessionUpdateMessage =
	CommentAgentSessionStatusSubscriptionType['response']['convoai_onAgentSessionUpdate'];
type OutcomeObservationSource = 'querySnapshot' | 'subscription' | 'polling';
type SafeErrorMessageTemplate =
	| 'A2A_ERROR'
	| 'ACCEPTABLE_USE_VIOLATIONS'
	| 'ERROR'
	| 'PRE_EXECUTION_ERROR';

function getSafeErrorMessageTemplate(value: unknown): SafeErrorMessageTemplate | undefined {
	switch (value) {
		case 'A2A_ERROR':
		case 'ACCEPTABLE_USE_VIOLATIONS':
		case 'ERROR':
		case 'PRE_EXECUTION_ERROR':
			return value;
		default:
			return undefined;
	}
}

/**
 * Fires an outcome event for an observed agent session state from the query snapshot,
 * subscription, or polling.
 * INPUT_REQUIRED, ARTEFACT_READY and COMPLETED count as success; FAILED counts as failure.
 * WORKING and CANCELLED are ignored. Success does not require the user to post the reply.
 *
 * Only ConvoAiErrorMessage supplies error details; polling provides a state without a message.
 * Callers apply the experiment gate and reject stale or locally cancelled updates.
 * Callers deduplicate success per conversation within the mounted hook; failures are not deduplicated.
 */
function fireCommentAgentSessionOutcomeEvent(
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	state: StreamState,
	observationSource: OutcomeObservationSource,
	message?: SessionUpdateMessage,
	contentType?: string,
) {
	if (state === 'WORKING' || state === 'CANCELLED') return;

	// A response awaiting user input counts as success, even before its action is completed.
	const action = state === 'FAILED' ? 'failed' : 'succeeded';
	const failureMessage =
		action === 'failed' && message?.__typename === 'ConvoAiErrorMessage' ? message : undefined;
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action,
				actionSubject: 'commentAgentSessionReply',
				source: 'agentMention',
				attributes:
					action === 'failed'
						? {
								...(contentType ? { contentType } : {}),
								outcomeState: state,
								observationSource,
								statusCode: failureMessage?.statusCode,
								errorMessageTemplate: getSafeErrorMessageTemplate(
									failureMessage?.errorMessageTemplate,
								),
							}
						: { ...(contentType ? { contentType } : {}), outcomeState: state, observationSource },
			},
		}).fire();
	} catch {
		// Analytics must not interrupt the agent session or its status UI.
	}
}

/**
 * Maps a single `convoai_onAgentSessionUpdate` message to a stream state, or null when the
 * message carries no status signal (and should be ignored). COMPLETED is kept as an internal
 * sentinel so the public state can become null and remove the status row.
 *
 * Only explicit backend failure signals map to FAILED — either a failed agent session state or
 * a ConvoAiErrorMessage. Transport/connection problems are handled by the subscription layer
 * and never surface as a (false) chat failure here.
 */
function mapMessageToState(message: SessionUpdateMessage | null | undefined): StreamState | null {
	switch (message?.__typename) {
		case 'ConvoAiTraceMessage':
			return 'WORKING';
		case 'ConvoAiConversationMessage':
			if (
				message.agentSessionState === 'CANCELLED' &&
				expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			) {
				return 'CANCELLED';
			}
			if (message.agentSessionState === 'COMPLETED' || message.agentSessionState === 'FAILED') {
				return message.agentSessionState;
			}
			// Only assistant messages reflect non-terminal agent progress.
			if (message.role !== 'ASSISTANT') {
				return null;
			}
			// Pending actions mean the user has to respond; otherwise the result is ready.
			return message.actions && message.actions.length > 0 ? 'INPUT_REQUIRED' : 'ARTEFACT_READY';
		case 'ConvoAiErrorMessage':
			return 'FAILED';
		default:
			// ConvoAiEmptyConversation and any future/unknown member: no status change.
			return null;
	}
}

export type SessionStateResult = {
	session: Omit<CommentAgentSession, 'sessionStatusRef'>;
	pollingEnabled: boolean;
	onPollRequest: Parameters<typeof pollCommentAgentSessions>[2];
};

/**
 * Subscribes to live status for a single comment's agent conversation and provides
 * its current state and polling configuration.
 *
 * Uses the `convoai_onAgentSessionUpdate` Relay subscription — an observe-only channel that
 * does not compete with the conversation's own message stream (owned by the CwR sidebar),
 * which is what made a direct SSE POST unreliable for freshly-mentioned agents.
 *
 * @param commentId - The comment this session belongs to; keys optimistic startup feedback.
 * @param conversationId - The Rovo conversationId; null until the BE association exists.
 * @param initialState - Optional snapshot state from `content.allCommentAgentSessions`. When
 *                       it is already terminal we surface it directly and skip the subscription.
 * @returns Session state and polling configuration — THINKING during optimistic startup, then CAP status.
 */
export function useCommentAgentSessionState(
	commentId: string,
	conversationId: string | undefined,
	initialState?: ConversationState,
	contentType?: string,
): SessionStateResult {
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLocallyCancelled = useIsConversationCancelled(
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? (conversationId ?? '')
			: '',
	);
	const isStarting = useOptimisticCommentAgentSession(
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ? commentId : '',
		!!conversationId || !!initialState,
	);

	// Deduplicate observations, independently of optimistic discovery. This resets on remount.
	const reportedSuccessConversationIds = useRef(new Set<string>());
	const handleSessionOutcome = useCallback(
		(
			next: StreamState,
			observationSource: OutcomeObservationSource,
			message?: SessionUpdateMessage,
		) => {
			if (next === 'WORKING' || next === 'CANCELLED') return;
			if (next !== 'FAILED') {
				if (!conversationId || reportedSuccessConversationIds.current.has(conversationId)) return;
				// Mark before firing so repeated subscription/poll updates cannot send success again.
				reportedSuccessConversationIds.current.add(conversationId);
			}
			fireCommentAgentSessionOutcomeEvent(
				createAnalyticsEvent,
				next,
				observationSource,
				message,
				contentType,
			);
		},
		[conversationId, createAnalyticsEvent, contentType],
	);
	const previousQuerySnapshot = useRef<{
		conversationId: string | undefined;
		state: ConversationState | undefined;
	}>();

	useEffect(() => {
		const previous = previousQuerySnapshot.current;
		previousQuerySnapshot.current = { conversationId, state: initialState };

		// The page-level query can observe the outcome before the subscription or poll does. Only
		// report a transition for the same conversation; an already-settled initial snapshot may
		// have been loaded during a page refresh and must not be counted as a new response.
		if (
			conversationId &&
			previous?.conversationId === conversationId &&
			previous.state !== initialState &&
			initialState &&
			initialState !== 'WORKING' &&
			!isLocallyCancelled &&
			expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
		) {
			handleSessionOutcome(initialState, 'querySnapshot');
		}
	}, [conversationId, initialState, isLocallyCancelled, handleSessionOutcome]);

	// Live status observed from the subscription. null until a message arrives. We never set
	// this from a transport/connection error — only explicit backend failure signals map to
	// FAILED — so a transient blip is never mistaken for a failed chat.
	const [streamState, setStreamState] = useState<StreamState | null>(null);
	const subscriptionRevision = useRef(0);
	const completionTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const completionPending = useRef(false);
	const pollingScope = useContext(AgentSessionPollingContext);

	// Reset observed state when the conversation changes, so a comment never briefly shows a
	// previous conversation's status.
	useEffect(() => {
		subscriptionRevision.current += 1;
		clearTimeout(completionTimer.current);
		completionPending.current = false;
		setStreamState(null);
		return () => {
			clearTimeout(completionTimer.current);
			subscriptionRevision.current += 1;
			completionPending.current = false;
		};
	}, [conversationId]);

	// Stopping in Chat is local to the conversation. Drop any prior live status so a later
	// user-initiated turn can resume from fresh subscription data after the cancellation clears.
	useEffect(() => {
		if (isLocallyCancelled) {
			subscriptionRevision.current += 1;
			clearTimeout(completionTimer.current);
			completionPending.current = false;
			setStreamState(null);
		}
	}, [isLocallyCancelled]);

	// Skip the subscription when there is nothing to observe, or when the backend snapshot is
	// already terminal (the agent finished in a prior session — show the snapshot directly).
	const snapshotIsTerminal = !!initialState && TERMINAL_STATES.has(initialState);
	// A delayed query must not close a subscription that has reported newer work.
	const skip =
		!cloudId ||
		!conversationId ||
		(snapshotIsTerminal &&
			(!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ||
				(streamState !== 'WORKING' && streamState !== 'INPUT_REQUIRED')));

	function recoverFromSubscriptionFailure() {
		if (
			skip ||
			isLocallyCancelled ||
			completionPending.current ||
			streamState === 'CANCELLED' ||
			streamState === 'COMPLETED' ||
			(streamState && TERMINAL_STATES.has(streamState)) ||
			!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
		)
			return;
		// Drop stale live status until live updates resume. Preserve a terminal message even
		// when it arrived in the same React batch as the transport completion.
		setStreamState((current) =>
			current === 'CANCELLED' ||
			current === 'COMPLETED' ||
			(current && TERMINAL_STATES.has(current))
				? current
				: null,
		);
		window.dispatchEvent(new Event(AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT));
	}

	const subscription = useSubscriptionInterop<
		CommentAgentSessionStatusSubscriptionType['response'],
		CommentAgentSessionStatusSubscriptionType['variables']
	>(CommentAgentSessionStatusSubscription, {
		skip,
		variables: { cloudId: cloudId ?? '', conversationId: conversationId ?? '' },
		interopApolloAggClient: true,
		onSubscriptionData: ({ subscriptionData }) => {
			const message = subscriptionData.data?.convoai_onAgentSessionUpdate;
			const next = mapMessageToState(message);
			if (next && !isLocallyCancelled) {
				const revision = ++subscriptionRevision.current;
				clearTimeout(completionTimer.current);
				completionPending.current = false;
				if (
					next === 'COMPLETED' &&
					conversationId &&
					pollingScope &&
					expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
				) {
					// A tool can complete while the conversation is still working. Allow a subsequent
					// stream update to win before querying the authoritative session state.
					completionPending.current = true;
					completionTimer.current = setTimeout(() => {
						completionTimer.current = undefined;
						void fetchCommentAgentSessionState(
							pollingScope.environment,
							pollingScope.pageId,
							conversationId,
						).then(
							(snapshot) => {
								if (subscriptionRevision.current !== revision) return;
								completionPending.current = false;
								if (snapshot) {
									handleSessionOutcome(snapshot, 'subscription');
									setStreamState(snapshot);
								}
							},
							() => {
								if (subscriptionRevision.current === revision) completionPending.current = false;
							},
						);
					}, COMPLETION_GRACE_MS);
				} else {
					if (expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
						handleSessionOutcome(next, 'subscription', message);
					}
					setStreamState(next);
				}
			}
		},
		...(expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? { onSubscriptionComplete: recoverFromSubscriptionFailure }
			: {}),
	});

	useEffect(() => {
		// The subscription hook exposes errors as results, so recovery follows that external update.
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		if (subscription?.error) recoverFromSubscriptionFailure();
		// An unchanged error must not discard live messages received after recovery.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscription?.error, conversationId]);

	// Prefer the live subscription status, fall back to the backend snapshot, and finally to
	// WORKING while a conversation exists but has not reported a status yet. A completed session
	// returns null so consumers remove its status row.
	const state: ConversationState | null =
		streamState === 'CANCELLED' || streamState === 'COMPLETED'
			? null
			: (streamState ?? initialState ?? (conversationId ? 'WORKING' : null));

	const onPollRequest = useCallback(() => {
		const revision = subscriptionRevision.current;
		return (snapshot: Map<string, StreamState>) => {
			const next = conversationId ? snapshot.get(conversationId) : undefined;
			if (next && subscriptionRevision.current === revision) {
				if (expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
					handleSessionOutcome(next, 'polling');
				}
				setStreamState(next);
			}
		};
	}, [conversationId, handleSessionOutcome]);
	const pollingEnabled =
		!!cloudId &&
		!!conversationId &&
		!isLocallyCancelled &&
		(state === 'WORKING' || state === 'INPUT_REQUIRED') &&
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true);

	const submission = useSubmittedCommentAgentAction(
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? (conversationId ?? '')
			: '',
	);
	// Keep submission progress or recovery visible until comments have been reconciled.
	const hasPendingSubmission = submission && submission.state.status !== 'resolved';
	let displayedState: CommentAgentSession['state'] = state ?? (isStarting ? 'THINKING' : null);
	if (hasPendingSubmission) {
		displayedState = 'INPUT_REQUIRED';
	}
	if (isLocallyCancelled || streamState === 'CANCELLED') {
		displayedState = null;
	}
	return {
		session: {
			state: displayedState,
			conversationId,
			agentIdentity: isStarting ? getOptimisticCommentAgentIdentity(commentId) : undefined,
		},
		pollingEnabled,
		onPollRequest,
	};
}

const ignoreSharedSessionPoll: SessionStateResult['onPollRequest'] = () => () => {};

/** Attaches visibility-based polling to a rendered comment's agent session status. */
export function useCommentAgentSession(
	commentId: string,
	conversationId: string | undefined,
	initialState?: ConversationState,
): CommentAgentSession {
	const shared = useContext(SharedCommentAgentSessionsContext);
	// Shared readers must not create another subscription, outcome reporter or recovery handler.
	const local = useCommentAgentSessionState(
		shared ? '' : commentId,
		shared ? undefined : conversationId,
		shared ? undefined : initialState,
	);
	const result = shared
		? (shared.states.get(commentId) ?? {
				session: { state: null, conversationId },
				pollingEnabled: false,
				onPollRequest: ignoreSharedSessionPoll,
			})
		: local;
	const sessionStatusRef = useCommentAgentSessionPolling(
		result.pollingEnabled,
		result.onPollRequest,
	);
	return { ...result.session, sessionStatusRef };
}
